// Live streaming run view. fn-1.29.
//
// **Acceptance**:
//   - "Opening `/runs/:id` shows agent messages incrementally as MA
//     events arrive."
//   - "Reconnect on Wi-Fi drop replays from `Last-Event-ID`." —
//     handled by the browser's `EventSource` semantics (the wrapper
//     in `streams/session-stream.ts` is transparent on reconnect).
//
// **Wiring**:
//   - Initial history: `trpc.runs.events.useQuery({ sessionId })` — one
//     shot fetch on mount. Seeds the reducer so prior turns paint
//     instantly even when the SSE handshake takes 1-2s.
//   - Live stream: `useSessionStream({ sessionId, seedEvents })` over
//     the BFF SSE re-fan. The hook handles reducer plumbing.
//   - Send-message: `trpc.runs.sendMessage.useMutation()`; on success
//     refetch nothing (the next SSE frame carries the user.message +
//     subsequent agent turns).
//   - Approve / reject the inline pending card: `trpc.approvals.decide
//     .useMutation()` driven by the gated tool's matching approval row.
//     We resolve approval-id via `trpc.approvals.list` filtered by
//     session id — see `usePendingApprovalForSession`.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { bffEndpoints } from '../../auth/bff-origin.js';
import { useClientOtel, useTrackUserAction } from '../../instrumentation/provider.js';
import { StatusPill } from '../../layout/status-pill.js';
import { buildSessionStreamUrl } from '../../streams/session-stream.js';
import { useSessionStream } from '../../streams/use-session-stream.js';
import { trpc } from '../../trpc-client.js';
import { formatClockTime, formatDelta, formatTime, truncateToolName } from '../format.js';

/**
 * Cross-origin SSE (fn-45.4): in deployed mode the EventSource must open
 * against the BFF origin (where the `__Host-` cookies live), not the static
 * SPA origin. We build the stream URL off `bffEndpoints().sseBaseUrl`
 * (absolute in deployed mode, relative via the Vite proxy in dev).
 */
function buildBffSessionStreamUrl(sessionId: string): string {
  return buildSessionStreamUrl(sessionId, bffEndpoints().sseBaseUrl);
}

export function RunDetailPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const sessionId = params.id ?? null;
  if (sessionId === null || sessionId.length === 0) {
    return (
      <section className="page page-run" role="alert">
        <h1>Session</h1>
        <p>Missing session id.</p>
      </section>
    );
  }
  return <RunDetailInner sessionId={sessionId} />;
}

type Tab = 'transcript' | 'debug';

function RunDetailInner({ sessionId }: { sessionId: string }): React.ReactElement {
  const sessionQuery = trpc.runs.byId.useQuery({ sessionId });
  const historyQuery = trpc.runs.events.useQuery({ sessionId });
  // fn-2.x — fetch the agent record so we can render its tool posture
  // (allow / deny / asks). Enabled once we know the agent id from the
  // session row.
  const agentId = sessionQuery.data?.agent_id;
  const agentQuery = trpc.agents.byId.useQuery(
    { agentId: agentId ?? '' },
    { enabled: agentId !== undefined && agentId.length > 0 },
  );

  // Memoize seed-events reference so the hook doesn't re-seed on every
  // render (would be a no-op via id dedup, but keeps React DevTools
  // honest).
  const seedEvents = useMemo(() => historyQuery.data?.data ?? [], [historyQuery.data]);

  const stream = useSessionStream({
    sessionId,
    seedEvents,
    // Open the EventSource against the BFF origin in deployed mode.
    urlBuilder: buildBffSessionStreamUrl,
  });

  const [tab, setTab] = useState<Tab>('transcript');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const selectedEvent = selectedEventId
    ? (stream.events.find((e) => e.id === selectedEventId) ?? null)
    : null;
  // fn-2.2 — bootstrap visibility toggle. The CP auto-injects a
  // `<system-bootstrap>` user.message on session create (carrying any
  // pasted CLAUDE.md / context document); the agent typically responds
  // with an ack ("I have processed the context…"). Both rows clutter
  // the operator's view of the real conversation, so hide them by
  // default and offer a toggle for debug.
  const [showBootstrap, setShowBootstrap] = useState(false);
  // fn-2.x — optimistic in-flight user message. The composer pushes
  // here on send; we clear when the real user.message lands on the
  // stream (matched by content). Avoids the visible 2–5 s gap
  // between "Send" and the SSE-delivered user.message that used to
  // make the SPA feel sluggish on the first turn of a session.
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  useEffect(() => {
    if (pendingUserMessage === null) return;
    const matched = stream.messages.some(
      (m) => m.kind === 'user' && m.content.trim() === pendingUserMessage.trim(),
    );
    if (matched) setPendingUserMessage(null);
  }, [stream.messages, pendingUserMessage]);

  const visibleMessages = useMemo(() => {
    const base = showBootstrap
      ? stream.messages
      : stream.messages.filter(
          (m) =>
            !(m.kind === 'user' && m.isBootstrap === true) &&
            !(m.kind === 'assistant' && m.isBootstrapAck === true),
        );
    if (pendingUserMessage === null) return base;
    return [
      ...base,
      {
        kind: 'user' as const,
        id: '__pending__',
        content: pendingUserMessage,
      },
    ];
  }, [stream.messages, showBootstrap, pendingUserMessage]);
  const hasBootstrap = stream.messages.some(
    (m) =>
      (m.kind === 'user' && m.isBootstrap === true) ||
      (m.kind === 'assistant' && m.isBootstrapAck === true),
  );

  return (
    <section className="page page-run">
      <header className="run-header">
        <h1>Session {sessionId.slice(0, 8)}…</h1>
        <div className="sb-chip-row">
          <StatusPill status={stream.status} />
          <span className="sb-chip">
            <code>
              {stream.connection === 'open'
                ? 'Live'
                : stream.connection === 'connecting'
                  ? 'Connecting…'
                  : 'Disconnected'}
            </code>
          </span>
          {sessionQuery.data?.agent_id !== undefined && (
            <span className="sb-chip" title={sessionQuery.data.agent_id}>
              <code>agent {sessionQuery.data.agent_id.slice(0, 8)}…</code>
            </span>
          )}
          {sessionQuery.data?.matter_id !== undefined && sessionQuery.data.matter_id !== null && (
            <span className="sb-chip" title={sessionQuery.data.matter_id}>
              <code>matter {sessionQuery.data.matter_id.slice(0, 8)}…</code>
            </span>
          )}
          {(() => {
            // Prefer the live aggregate from the stream's
            // span.model_request_end events. Fall back to the durable
            // sessions.cost_used_tokens column (which will be wired by
            // the usage webhook handler in a follow-up). Either way,
            // render a single chip + a tooltip with the breakdown.
            const liveTotal =
              stream.tokens.input +
              stream.tokens.output +
              stream.tokens.cacheRead +
              stream.tokens.cacheCreate;
            const total =
              liveTotal > 0
                ? liveTotal
                : typeof sessionQuery.data?.cost_used_tokens === 'number'
                  ? sessionQuery.data.cost_used_tokens
                  : 0;
            if (total === 0) return null;
            const breakdown =
              liveTotal > 0
                ? `↓ in ${stream.tokens.input.toLocaleString()}\n↑ out ${stream.tokens.output.toLocaleString()}\n♻ cache read ${stream.tokens.cacheRead.toLocaleString()}\n+ cache write ${stream.tokens.cacheCreate.toLocaleString()}`
                : 'Durable count from sessions.cost_used_tokens';
            return (
              <span className="sb-chip" title={breakdown}>
                {total.toLocaleString()} tokens
              </span>
            );
          })()}
          {sessionQuery.data?.created_at !== undefined && (
            <span className="sb-chip">started {formatTime(sessionQuery.data.created_at)}</span>
          )}
        </div>
        <AgentToolsPanel agent={agentQuery.data ?? null} />
      </header>
      {stream.lastError !== null && (
        <div className="run-error" role="alert">
          <strong>{stream.lastError.type}</strong>: {stream.lastError.message}
        </div>
      )}

      <nav className="sb-tabs" role="tablist" aria-label="Session view">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'transcript'}
          className="sb-tab"
          onClick={() => setTab('transcript')}
        >
          Transcript ({visibleMessages.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'debug'}
          className="sb-tab"
          onClick={() => setTab('debug')}
        >
          Debug ({stream.events.length})
        </button>
        {tab === 'transcript' && hasBootstrap && (
          <label
            className="sb-tab-toggle"
            title="Show the auto-injected context message + agent ack"
          >
            <input
              type="checkbox"
              checked={showBootstrap}
              onChange={(e) => setShowBootstrap(e.target.checked)}
            />
            <span>Show bootstrap</span>
          </label>
        )}
      </nav>

      <div className={`run-body${selectedEvent === null ? ' run-body--collapsed-detail' : ''}`}>
        <div className="run-events-col">
          {tab === 'transcript' ? (
            <TranscriptView
              messages={visibleMessages}
              events={stream.events}
              selectedEventId={selectedEventId}
              onSelect={(id) => setSelectedEventId(id === selectedEventId ? null : id)}
            />
          ) : (
            <DebugView
              events={stream.events}
              selectedEventId={selectedEventId}
              onSelect={(id) => setSelectedEventId(id === selectedEventId ? null : id)}
            />
          )}
          <AgentThinkingIndicator
            status={stream.status}
            messages={visibleMessages}
            pendingApproval={stream.pendingApproval}
          />
          {stream.pendingApproval !== null && (
            <PendingApprovalCard
              sessionId={sessionId}
              toolUseId={stream.pendingApproval.toolUseId}
              toolName={stream.pendingApproval.toolName}
              input={stream.pendingApproval.input}
            />
          )}
          <RunComposer
            sessionId={sessionId}
            disabled={stream.status === 'terminated' || stream.pendingApproval !== null}
            disabledReason={
              stream.status === 'terminated'
                ? 'Session terminated'
                : stream.pendingApproval !== null
                  ? 'Approve or reject the pending tool to continue'
                  : null
            }
            onOptimisticSend={(c) => setPendingUserMessage(c)}
            onOptimisticClear={() => setPendingUserMessage(null)}
          />
        </div>
        {selectedEvent !== null && (
          <aside className="run-detail-col" aria-label="Event details">
            <header>
              <span className="sb-chip">
                <code>{selectedEvent.type}</code>
              </span>
              <button
                type="button"
                className="run-detail-close"
                onClick={() => setSelectedEventId(null)}
                aria-label="Close event details"
              >
                ×
              </button>
            </header>
            <dl>
              <dt>Event ID</dt>
              <dd>{selectedEvent.id}</dd>
            </dl>
            {selectedEvent.data !== undefined && selectedEvent.data !== null && (
              <pre>{JSON.stringify(selectedEvent.data, null, 2)}</pre>
            )}
          </aside>
        )}
      </div>
    </section>
  );
}

function AgentThinkingIndicator({
  status,
  messages,
  pendingApproval,
}: {
  status: string;
  messages: ReturnType<typeof useSessionStream>['messages'];
  pendingApproval: ReturnType<typeof useSessionStream>['pendingApproval'];
}): React.ReactElement | null {
  // Show a subtle "Agent is thinking…" hint when the session is
  // running and the last message is from the user (or a tool result
  // we're waiting on the agent to react to). Suppressed during a HITL
  // gate — the approval card is the more useful indicator there.
  if (pendingApproval !== null) return null;
  if (status !== 'running') return null;
  const last = messages.length > 0 ? messages[messages.length - 1] : undefined;
  if (last !== undefined && last.kind === 'assistant') return null;
  return (
    <div className="agent-thinking" role="status" aria-live="polite">
      <span className="agent-thinking-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span>Agent is thinking…</span>
    </div>
  );
}

function TranscriptView({
  messages,
  events,
  selectedEventId,
  onSelect,
}: {
  messages: ReturnType<typeof useSessionStream>['messages'];
  events: ReturnType<typeof useSessionStream>['events'];
  selectedEventId: string | null;
  onSelect: (id: string) => void;
}): React.ReactElement {
  // Auto-scroll to bottom on new message. Cheap + chat-native: when a
  // new message lands we scroll the page so the latest row is in view.
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string | null>(null);
  useEffect(() => {
    const lastMsg = messages.length > 0 ? messages[messages.length - 1] : undefined;
    const lastId = lastMsg?.id ?? null;
    if (lastId !== null && lastId !== lastIdRef.current) {
      lastIdRef.current = lastId;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <p className="run-detail-col-empty" style={{ padding: '1rem 0.5rem' }}>
        Waiting for the first event…
      </p>
    );
  }
  // Lookup map for timing — message id mirrors the originating event id
  // (the reducer sets msg.id = ev.id). Use the event's createdAt to
  // render per-message timestamp + delta from the previous message.
  const createdAtByEventId = new Map(events.map((e) => [e.id, e.createdAt ?? '']));
  return (
    <ol className="run-messages" data-testid="run-messages">
      {messages.map((msg, idx) => {
        const createdAt = createdAtByEventId.get(msg.id);
        const prev = idx > 0 ? messages[idx - 1] : undefined;
        const prevCreatedAt = prev ? createdAtByEventId.get(prev.id) : undefined;
        const delta = formatDelta(prevCreatedAt, createdAt);
        return (
          <li
            key={msg.id}
            className={`run-message run-message--${msg.kind}`}
            aria-selected={msg.id === selectedEventId}
            onClick={() => onSelect(msg.id)}
          >
            <span className={`run-message-role run-message-role--${msg.kind}`}>
              {messageRoleLabel(msg.kind)}
            </span>
            <div className="run-message-body">
              <MessageBody message={msg} />
            </div>
            <span className="run-message-meta">
              {createdAt !== undefined && createdAt.length > 0 && (
                <time dateTime={createdAt} title={createdAt}>
                  {formatClockTime(createdAt)}
                </time>
              )}
              {delta.length > 0 && (
                <small className="run-message-delta" title={'Time since previous message'}>
                  {delta}
                </small>
              )}
              <code>{msg.id.slice(0, 10)}…</code>
            </span>
          </li>
        );
      })}
      <div ref={bottomRef} aria-hidden="true" />
    </ol>
  );
}

function DebugView({
  events,
  selectedEventId,
  onSelect,
}: {
  events: ReturnType<typeof useSessionStream>['events'];
  selectedEventId: string | null;
  onSelect: (id: string) => void;
}): React.ReactElement {
  if (events.length === 0) {
    return (
      <p className="run-detail-col-empty" style={{ padding: '1rem 0.5rem' }}>
        No events yet.
      </p>
    );
  }
  return (
    <ol className="run-messages" data-testid="run-raw-events">
      {events.map((ev, idx) => {
        const prev = idx > 0 ? events[idx - 1] : undefined;
        const delta = formatDelta(prev?.createdAt, ev.createdAt);
        return (
          <li
            key={ev.id}
            className="run-message run-message--debug"
            aria-selected={ev.id === selectedEventId}
            onClick={() => onSelect(ev.id)}
          >
            <span className="run-message-role">{ev.type.split('.')[0]}</span>
            <div className="run-message-body">
              <p>
                <code>{ev.type}</code>
              </p>
            </div>
            <span className="run-message-meta">
              {ev.createdAt !== undefined && (
                <time dateTime={ev.createdAt} title={ev.createdAt}>
                  {formatClockTime(ev.createdAt)}
                </time>
              )}
              {delta.length > 0 && <small className="run-message-delta">{delta}</small>}
              <code>{ev.id.slice(0, 10)}…</code>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * fn-2.x — render the agent's tool posture on the session header so
 * operators can see at a glance what the agent can and cannot do.
 *
 * **Source shape** (per `agents.config.tools[*]`):
 *   - Each toolset entry has `configs[*]` (per-tool override) and a
 *     `default_config` (the policy for anything not in `configs`).
 *   - `permission_policy.type` is one of `always_allow`, `always_ask`,
 *     `always_deny` (verified 2026-05-17 against MA wire).
 *   - When `configs` is absent or empty, the chip row shows just the
 *     default policy + the toolset id.
 *
 * The panel collapses when the config is empty / unparseable; we don't
 * want to render a confusing "No tools" line for the common case where
 * the API returns an agent we haven't yet wired tool-policy reading for.
 */
function AgentToolsPanel({
  agent,
}: {
  agent: { config: Record<string, unknown> | null; name: string } | null;
}): React.ReactElement | null {
  if (agent === null) return null;
  const config = agent.config;
  if (config === null || typeof config !== 'object') return null;
  const tools = (config as { tools?: unknown }).tools;
  if (!Array.isArray(tools) || tools.length === 0) return null;

  const rows: Array<{
    toolset: string;
    defaultPolicy: ToolPolicy;
    overrides: Array<{ name: string; policy: ToolPolicy }>;
  }> = [];
  for (const t of tools) {
    if (t === null || typeof t !== 'object') continue;
    const tt = t as {
      type?: unknown;
      default_config?: unknown;
      configs?: unknown;
    };
    const toolsetName = typeof tt.type === 'string' ? tt.type : 'tools';
    const defaultPolicy = readPolicy(tt.default_config);
    const overrides: Array<{ name: string; policy: ToolPolicy }> = [];
    if (Array.isArray(tt.configs)) {
      for (const c of tt.configs) {
        if (c === null || typeof c !== 'object') continue;
        const cc = c as { name?: unknown; permission_policy?: unknown };
        if (typeof cc.name !== 'string') continue;
        overrides.push({
          name: cc.name,
          policy: readPolicyValue(cc.permission_policy),
        });
      }
    }
    rows.push({ toolset: toolsetName, defaultPolicy, overrides });
  }
  if (rows.length === 0) return null;

  return (
    <div className="agent-tools-panel" aria-label={`Tool posture for ${agent.name}`}>
      <span className="agent-tools-panel-label">Tools</span>
      {rows.map((row) => (
        <Fragment key={row.toolset}>
          <span className="agent-tools-panel-toolset" title={row.toolset}>
            {humanToolsetName(row.toolset)} default:
          </span>
          <PolicyChip policy={row.defaultPolicy} />
          {row.overrides.map((o) => (
            <PolicyChip key={`${row.toolset}/${o.name}`} policy={o.policy} toolName={o.name} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

type ToolPolicy = 'allow' | 'ask' | 'deny' | 'unknown';

function readPolicy(defaultConfig: unknown): ToolPolicy {
  if (defaultConfig === null || typeof defaultConfig !== 'object') return 'unknown';
  return readPolicyValue((defaultConfig as { permission_policy?: unknown }).permission_policy);
}
function readPolicyValue(p: unknown): ToolPolicy {
  if (p === null || typeof p !== 'object') return 'unknown';
  const t = (p as { type?: unknown }).type;
  if (t === 'always_allow') return 'allow';
  if (t === 'always_ask') return 'ask';
  if (t === 'always_deny') return 'deny';
  return 'unknown';
}
function humanToolsetName(name: string): string {
  // Strip date suffixes like _20260401 for readability.
  return name.replace(/_\d{8}$/, '').replace(/_/g, ' ');
}

function PolicyChip({
  policy,
  toolName,
}: {
  policy: ToolPolicy;
  toolName?: string;
}): React.ReactElement {
  const glyph = policy === 'allow' ? '✓' : policy === 'ask' ? '⏸' : policy === 'deny' ? '✗' : '·';
  const label =
    policy === 'allow'
      ? 'Auto-allow'
      : policy === 'ask'
        ? 'Asks'
        : policy === 'deny'
          ? 'Deny'
          : 'Unknown';
  return (
    <span
      className={`agent-tool-chip agent-tool-chip--${policy}`}
      title={`${toolName ?? 'default'}: ${label}`}
    >
      {glyph} {toolName ?? label}
      {toolName !== undefined && <span className="agent-tool-chip-policy"> · {label}</span>}
    </span>
  );
}

/**
 * fn-2.x — collapsible disclosure for Claude's extended-thinking
 * content. Default hidden so the assistant turn renders compact;
 * one click reveals the model's pre-answer reasoning. Italicised +
 * dimmer so it's clearly separated from the answer itself.
 */
function ThinkingDisclosure({ text }: { text: string }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="run-message-thinking">
      <button
        type="button"
        className="run-message-thinking-toggle"
        onClick={(e) => {
          // Don't propagate to the message row's onSelect handler.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
      >
        💭 {open ? 'Hide' : 'Show'} thinking ({text.length.toLocaleString()} chars)
      </button>
      {open && <pre className="run-message-thinking-body">{text}</pre>}
    </div>
  );
}

/**
 * fn-2.x — compact transcript renderer for `ask_user_question`
 * tool_use rows. Shows the question count + the question texts as
 * a bullet list (collapsed by default for ≥3 questions so the
 * transcript stays tidy). When the operator's answers have been
 * submitted (result is set), shows them as a "Q → A" summary.
 *
 * Why a bespoke renderer: the raw input is a 50+ line JSON dump of
 * nested option objects; the wizard form below the row already
 * surfaces every option interactively, so duplicating that as JSON
 * adds noise without information.
 */
function AskUserQuestionSummary({
  questions,
  answers,
}: {
  questions: ReadonlyArray<AskUserQuestionShape>;
  answers: Record<string, string | readonly string[]> | null;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(questions.length < 3);
  return (
    <div className="run-tool-askuser">
      <button
        type="button"
        className="run-tool-askuser-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
      >
        {expanded ? '▾' : '▸'} {questions.length} question{questions.length === 1 ? '' : 's'} asked
        {answers !== null && <span className="run-tool-askuser-answered"> · answered</span>}
      </button>
      {expanded && (
        <ol className="run-tool-askuser-list">
          {questions.map((q) => {
            const ans = answers?.[q.question];
            const ansText = Array.isArray(ans)
              ? ans.join(', ')
              : typeof ans === 'string'
                ? ans
                : null;
            return (
              <li key={q.question}>
                {q.header !== undefined && q.header.length > 0 && (
                  <span className="run-tool-askuser-header">{q.header}</span>
                )}
                <span className="run-tool-askuser-q">{q.question}</span>
                {ansText !== null && (
                  <div className="run-tool-askuser-a">
                    <span className="run-tool-askuser-a-arrow">↳</span>
                    <span className="run-tool-askuser-a-text">{ansText}</span>
                  </div>
                )}
                {ansText === null && (
                  <ul className="run-tool-askuser-options">
                    {q.options.map((o) => (
                      <li key={o.label} title={o.description}>
                        {o.label}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function parseAskUserInput(
  input: Record<string, unknown>,
): ReadonlyArray<AskUserQuestionShape> | null {
  const qs = (input as { questions?: unknown }).questions;
  if (!Array.isArray(qs)) return null;
  const out: AskUserQuestionShape[] = [];
  for (const q of qs) {
    if (q === null || typeof q !== 'object') continue;
    const qq = q as {
      question?: unknown;
      header?: unknown;
      options?: unknown;
      multiSelect?: unknown;
    };
    if (typeof qq.question !== 'string' || !Array.isArray(qq.options)) continue;
    const options: AskUserQuestionOption[] = [];
    for (const o of qq.options) {
      if (o === null || typeof o !== 'object') continue;
      const oo = o as { label?: unknown; description?: unknown };
      if (typeof oo.label !== 'string') continue;
      options.push({
        label: oo.label,
        ...(typeof oo.description === 'string' ? { description: oo.description } : {}),
      });
    }
    out.push({
      question: qq.question,
      options,
      ...(typeof qq.header === 'string' ? { header: qq.header } : {}),
      ...(typeof qq.multiSelect === 'boolean' ? { multiSelect: qq.multiSelect } : {}),
    });
  }
  return out.length > 0 ? out : null;
}

/**
 * The tool_result payload for ask_user_question is the form-data the
 * operator submitted, JSON-stringified by MA. Try to recover the
 * `answers` map; return null on any shape mismatch (compact card
 * silently falls back to the per-question option list).
 */
function parseAskUserAnswers(output: string): Record<string, string | readonly string[]> | null {
  try {
    const parsed = JSON.parse(output);
    const a = (parsed as { answers?: unknown }).answers;
    if (a === null || typeof a !== 'object' || Array.isArray(a)) return null;
    const result: Record<string, string | readonly string[]> = {};
    for (const [k, v] of Object.entries(a as Record<string, unknown>)) {
      if (typeof v === 'string') result[k] = v;
      else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
        result[k] = v as readonly string[];
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}

function messageRoleLabel(kind: string): string {
  switch (kind) {
    case 'user':
      return 'User';
    case 'assistant':
      return 'Agent';
    case 'tool_use':
      return 'Tool';
    case 'tool_result':
      return 'Result';
    case 'tool_confirmation':
      return 'Decision';
    default:
      return kind;
  }
}

/**
 * Collapsible raw-events panel — every event MA emits, in arrival
 * order. Useful when the curated message list dropped something
 * (we don't render every event kind nicely) and you need to see
 * the wire shape.
 *
 * Defaults to hidden because the volume is high on busy runs; the
 * count badge makes it obvious there's more to see.
 */
function MessageBody({
  message,
}: {
  message: ReturnType<typeof useSessionStream>['messages'][number];
}): React.ReactElement {
  switch (message.kind) {
    case 'user':
      return <p>{message.content}</p>;
    case 'assistant':
      return (
        <Fragment>
          {message.thinking !== undefined && message.thinking.length > 0 && (
            <ThinkingDisclosure text={message.thinking} />
          )}
          <p>{message.content}</p>
          {message.usage !== undefined &&
            (message.usage.input !== undefined || message.usage.output !== undefined) && (
              <small className="run-message-usage" title="Token usage on this turn">
                {message.usage.input !== undefined && (
                  <span>↓ {message.usage.input.toLocaleString()} in</span>
                )}
                {message.usage.output !== undefined && (
                  <span>↑ {message.usage.output.toLocaleString()} out</span>
                )}
              </small>
            )}
        </Fragment>
      );
    case 'tool_use': {
      // fn-2.x — ask_user_question gets a bespoke compact card. The
      // raw JSON dump (~50 lines of nested options) drowns the
      // transcript; the wizard form below already shows everything
      // the operator needs to interact with. Here we just summarise:
      // "Asked N questions" + a bullet list of the question texts.
      const isAskUser = message.toolName === 'ask_user_question';
      const askInput = isAskUser ? parseAskUserInput(message.input) : null;
      return (
        <Fragment>
          <p>
            <span className="run-tool-name" title={message.toolName}>
              {truncateToolName(message.toolName)}
            </span>
            {message.decision !== undefined && (
              <span
                className={`tool-decision tool-decision--${message.decision}`}
                title={
                  message.decidedBy !== undefined
                    ? `${message.decision === 'allow' ? 'Approved' : 'Denied'} by ${message.decidedBy}`
                    : undefined
                }
              >
                {message.decision === 'allow' ? '✓ Approved' : '✗ Denied'}
                {message.decidedAt !== undefined && (
                  <span className="tool-decision-time"> · {formatTime(message.decidedAt)}</span>
                )}
                {message.decidedBy !== undefined && (
                  <span className="tool-decision-by"> · by {message.decidedBy.slice(0, 8)}…</span>
                )}
                {message.denyMessage !== undefined && (
                  <span className="tool-decision-msg"> — {message.denyMessage}</span>
                )}
              </span>
            )}
          </p>
          {isAskUser && askInput !== null ? (
            <AskUserQuestionSummary
              questions={askInput}
              answers={
                message.result !== undefined ? parseAskUserAnswers(message.result.output) : null
              }
            />
          ) : (
            <pre className="run-tool-input">{JSON.stringify(message.input, null, 2)}</pre>
          )}
          {!isAskUser && message.result !== undefined && (
            <div
              className={`run-tool-result-bundled${message.result.isError === true ? ' run-tool-result-bundled--error' : ''}`}
            >
              <p className="run-tool-result-bundled-label">
                {message.result.isError === true ? '✗ Error' : '↳ Output'}
              </p>
              <pre className="run-tool-output">{message.result.output}</pre>
            </div>
          )}
        </Fragment>
      );
    }
    case 'tool_result':
      return (
        <Fragment>
          <p>{message.isError === true ? 'Error' : 'Output'}</p>
          <pre className="run-tool-output">{message.output}</pre>
        </Fragment>
      );
    case 'tool_confirmation': {
      const verdict = message.decision === 'allow' ? 'Approved' : 'Denied';
      const who = message.decidedBy !== undefined ? ` by ${message.decidedBy}` : '';
      const when = message.decidedAt !== undefined ? ` at ${formatTime(message.decidedAt)}` : '';
      return (
        <p>
          {verdict}
          {who}
          {when}
          {message.denyMessage !== undefined ? ` — ${message.denyMessage}` : null}
        </p>
      );
    }
    default:
      return <p>?</p>;
  }
}

interface PendingApprovalCardProps {
  readonly sessionId: string;
  readonly toolUseId: string;
  readonly toolName: string;
  readonly input: Record<string, unknown>;
}

/**
 * Pending HITL approval card. The control plane row id (approval id)
 * isn't carried in the MA event; we resolve it by listing the
 * session's pending approvals and matching `provider_tool_use_id`.
 *
 * **Why the lookup happens inline rather than on the stream-state**:
 * the approval row's id is a DB-generated UUID assigned when the HITL
 * daemon writes the row — independent of any MA event payload. The
 * SPA must call `approvals.list` to learn it. Doing so HERE (inside the
 * card) means the lookup only happens when a card is visible.
 */
function PendingApprovalCard(props: PendingApprovalCardProps): React.ReactElement {
  // Per-session listing (control plane has `GET /sessions/:id/approvals`,
  // but our BFF only exposes the cross-session `approvals.list`. Filter
  // client-side by session id.)
  const approvalsQuery = trpc.approvals.list.useQuery(
    {},
    {
      refetchInterval: 3_000,
    },
  );
  const approval = approvalsQuery.data?.data.find(
    (a) => a.session_id === props.sessionId && a.provider_tool_use_id === props.toolUseId,
  );
  const decideMutation = trpc.approvals.decide.useMutation();
  const utils = trpc.useUtils();
  const trackUserAction = useTrackUserAction();
  const otel = useClientOtel();
  // fn-1.31 round 7 — prime the platform-sessionId trace cache so the
  // approve/reject click emits its `spa.approval.*` span synchronously
  // on `hash(props.sessionId)`. Matches the wiring in
  // `pages/approvals/list.tsx`'s `ApprovalRow` — same correlator, same
  // cross-tier stitch. Fire-and-forget; idempotent.
  useEffect(() => {
    if (otel !== null) {
      void otel.primeSessionTrace(props.sessionId);
    }
  }, [otel, props.sessionId]);

  const handle = async (
    decision: 'approved' | 'rejected',
    denyMessage?: string,
    formData?: Record<string, unknown>,
  ): Promise<void> => {
    if (approval === undefined) return;
    // fn-1.31 round 7 — emit the SPA click span before the mutation,
    // anchored on the platform sessionId so the BFF + control-plane
    // spans nest on the same Langfuse trace. Matches the call shape in
    // `pages/approvals/list.tsx`'s `ApprovalRow`.
    trackUserAction({
      name: `spa.approval.${decision === 'approved' ? 'approve' : 'reject'}`,
      sessionId: props.sessionId,
      metadata: {
        approval_id: approval.id,
        session_id: props.sessionId,
        tool_name: props.toolName,
        tool_use_id: props.toolUseId,
      },
    });
    try {
      await decideMutation.mutateAsync({
        sessionId: props.sessionId,
        approvalId: approval.id,
        decision,
        ...(denyMessage !== undefined && denyMessage.length > 0 ? { denyMessage } : {}),
        ...(formData !== undefined ? { formData } : {}),
      });
      // Refresh the badge + list immediately rather than waiting for the
      // next poll tick. The session stream will receive the matching
      // `user.tool_confirmation` event via SSE in a moment.
      await Promise.all([
        utils.approvals.pendingCount.invalidate(),
        utils.approvals.list.invalidate(),
      ]);
    } catch {
      // Mutation error surfaces via decideMutation.error below.
    }
  };

  return (
    <section
      aria-label="Tool approval required"
      className="pending-approval-card"
      data-testid="pending-approval-card"
    >
      <header className="pending-approval-card-header">
        <strong>Approval required</strong>
        <span className="pending-approval-card-tool">{props.toolName}</span>
      </header>
      {/* fn-2.x — render the ask_user_question form inline when the
          approval row is the elicitation kind. The control-plane
          `validateAskUserAnswers` accepts free-text per the Agent SDK
          "Other" pattern, so each question gets the declared options
          plus an "Other (type your own)" row with a text input. */}
      {props.toolName === 'ask_user_question' &&
      approval !== undefined &&
      approval.ack_kind === 'custom_tool_result_immediate' ? (
        <AskUserQuestionForm
          input={props.input}
          isPending={decideMutation.isPending}
          onSubmit={(formData) => void handle('approved', undefined, formData)}
          onReject={() => void handle('rejected')}
        />
      ) : (
        <>
          <pre className="pending-approval-card-input">{JSON.stringify(props.input, null, 2)}</pre>
          <div className="pending-approval-card-actions">
            <button
              type="button"
              onClick={() => void handle('approved')}
              disabled={
                approval === undefined || decideMutation.isPending || approvalsQuery.isPending
              }
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => void handle('rejected')}
              disabled={
                approval === undefined || decideMutation.isPending || approvalsQuery.isPending
              }
            >
              Reject
            </button>
            {approval === undefined && approvalsQuery.error === null && (
              <small className="pending-approval-card-note">Looking up approval id…</small>
            )}
            {approvalsQuery.error !== null && (
              <>
                <div className="pending-approval-card-error" role="alert">
                  Failed to load approval id: {approvalsQuery.error.message}
                </div>
                <button
                  type="button"
                  onClick={() => void approvalsQuery.refetch()}
                  disabled={approvalsQuery.isFetching}
                >
                  Retry
                </button>
              </>
            )}
          </div>
        </>
      )}
      {decideMutation.error !== null && (
        <p className="pending-approval-card-error" role="alert">
          {decideMutation.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * fn-2.x — `ask_user_question` elicitation form. Renders the agent's
 * 1-4 questions as radio (single-select) or checkbox (multi-select)
 * groups, plus an "Other (type your own)" row that accepts free text
 * per the Agent SDK convention. On submit, builds the
 * `{questions, answers}` payload validated by
 * `validateAskUserAnswers` (CP-side) and sends it as `formData` on
 * approvals.decide.
 *
 * Schema verified against the Agent SDK docs (2026-05-17):
 *   https://code.claude.com/docs/en/agent-sdk/user-input
 */
interface AskUserQuestionOption {
  readonly label: string;
  readonly description?: string;
}
interface AskUserQuestionShape {
  readonly question: string;
  readonly header?: string;
  readonly options: readonly AskUserQuestionOption[];
  readonly multiSelect?: boolean;
}
function AskUserQuestionForm({
  input,
  isPending,
  onSubmit,
  onReject,
}: {
  input: Record<string, unknown>;
  isPending: boolean;
  onSubmit: (formData: Record<string, unknown>) => void;
  onReject: () => void;
}): React.ReactElement {
  // fn-2.x (CodeRabbit) — use the same defensive parser the
  // transcript renderer uses (parseAskUserInput) so malformed
  // payloads can't crash the render path with a missing-options
  // `.map` or null-options access. Empty array → form bails to a
  // helpful "No questions" branch below.
  const questions = parseAskUserInput(input) ?? [];
  // Wizard step (0-indexed). Default to first question; advances via
  // Next button, reverts via Back. Last question's Next becomes Submit.
  const [step, setStep] = useState(0);
  // Per-question state. For single-select: a string (label or
  // free-text). For multi-select: a string array. The "Other" flag
  // tracks whether the operator picked the free-text row; the
  // accompanying text input value is stored independently so toggling
  // off Other doesn't wipe their typing.
  const [singleChoice, setSingleChoice] = useState<Record<string, string>>({});
  const [otherActive, setOtherActive] = useState<Record<string, boolean>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [multiChoice, setMultiChoice] = useState<Record<string, Set<string>>>({});

  const isAnswered = (q: AskUserQuestionShape): boolean => {
    if (q.multiSelect === true) {
      const picks = multiChoice[q.question];
      const otherT = otherActive[q.question] === true ? (otherText[q.question] ?? '').trim() : '';
      const total = (picks?.size ?? 0) + (otherT.length > 0 ? 1 : 0);
      return total > 0;
    }
    if (otherActive[q.question] === true) {
      return (otherText[q.question] ?? '').trim().length > 0;
    }
    return (singleChoice[q.question] ?? '').length > 0;
  };
  const allAnswered = questions.every(isAnswered);
  const isLast = step === questions.length - 1;
  const currentQuestion = questions[step];
  const currentAnswered = currentQuestion !== undefined ? isAnswered(currentQuestion) : false;

  const buildPayload = (): Record<string, unknown> => {
    const answers: Record<string, string | readonly string[]> = {};
    for (const q of questions) {
      const otherT = otherActive[q.question] === true ? (otherText[q.question] ?? '').trim() : '';
      if (q.multiSelect === true) {
        const labels = [...(multiChoice[q.question] ?? new Set<string>())];
        if (otherT.length > 0) labels.push(otherT);
        answers[q.question] = labels;
      } else if (otherT.length > 0) {
        answers[q.question] = otherT;
      } else {
        answers[q.question] = singleChoice[q.question] ?? '';
      }
    }
    return { questions, answers };
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!allAnswered) return;
    onSubmit(buildPayload());
  };

  if (currentQuestion === undefined) {
    return <p>No questions provided.</p>;
  }
  const q = currentQuestion;
  const inputName = `q-${step}`;

  return (
    <form className="ask-user-form" onSubmit={submit}>
      {/* Wizard progress: step n of N + dot indicator. */}
      <div className="ask-user-form-progress">
        <span className="ask-user-form-progress-text">
          Question {step + 1} of {questions.length}
        </span>
        <div className="ask-user-form-progress-dots" role="presentation">
          {questions.map((qq, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: each question slot is positional, the array is fixed-length per session
              key={i}
              className={`ask-user-form-progress-dot${
                i === step ? ' ask-user-form-progress-dot--active' : ''
              }${isAnswered(qq) ? ' ask-user-form-progress-dot--done' : ''}`}
            />
          ))}
        </div>
      </div>
      <fieldset className="ask-user-form-question">
        <legend className="ask-user-form-question-legend">
          {q.header !== undefined && q.header.length > 0 && (
            <span className="ask-user-form-question-header">{q.header}</span>
          )}
          <span className="ask-user-form-question-text">{q.question}</span>
          {q.multiSelect === true && (
            <span className="ask-user-form-question-hint">(pick one or more)</span>
          )}
        </legend>
        <div className="ask-user-form-options">
          {q.options.map((opt, oi) => {
            const optId = `${inputName}-opt-${oi}`;
            const isMulti = q.multiSelect === true;
            const checked = isMulti
              ? multiChoice[q.question]?.has(opt.label) === true
              : singleChoice[q.question] === opt.label;
            return (
              <label
                key={optId}
                htmlFor={optId}
                className={`ask-user-form-option${checked ? ' ask-user-form-option--checked' : ''}`}
              >
                <input
                  id={optId}
                  type={isMulti ? 'checkbox' : 'radio'}
                  name={inputName}
                  checked={checked}
                  onChange={(e) => {
                    if (isMulti) {
                      setMultiChoice((prev) => {
                        const next = { ...prev };
                        const set = new Set(next[q.question] ?? []);
                        if (e.target.checked) set.add(opt.label);
                        else set.delete(opt.label);
                        next[q.question] = set;
                        return next;
                      });
                    } else {
                      setSingleChoice((prev) => ({ ...prev, [q.question]: opt.label }));
                      setOtherActive((prev) => ({ ...prev, [q.question]: false }));
                    }
                  }}
                />
                <span className="ask-user-form-option-label">{opt.label}</span>
                {opt.description !== undefined && opt.description.length > 0 && (
                  <span className="ask-user-form-option-description">{opt.description}</span>
                )}
              </label>
            );
          })}
          {/* "Other" free-text row — Agent SDK convention. */}
          <label
            htmlFor={`${inputName}-other`}
            className={`ask-user-form-option ask-user-form-option--other${otherActive[q.question] === true ? ' ask-user-form-option--checked' : ''}`}
          >
            <input
              id={`${inputName}-other`}
              type={q.multiSelect === true ? 'checkbox' : 'radio'}
              name={inputName}
              checked={otherActive[q.question] === true}
              onChange={(e) => {
                setOtherActive((prev) => ({ ...prev, [q.question]: e.target.checked }));
                if (!q.multiSelect && e.target.checked) {
                  setSingleChoice((prev) => ({ ...prev, [q.question]: '' }));
                }
              }}
            />
            <span className="ask-user-form-option-label">Other (type your own)</span>
            {otherActive[q.question] === true && (
              <input
                type="text"
                className="ask-user-form-other-input"
                placeholder="Type your answer…"
                value={otherText[q.question] ?? ''}
                onChange={(e) =>
                  setOtherText((prev) => ({ ...prev, [q.question]: e.target.value }))
                }
              />
            )}
          </label>
        </div>
      </fieldset>
      <div className="ask-user-form-actions">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0 || isPending}
          className="ask-user-form-back"
        >
          ← Back
        </button>
        {!isLast ? (
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(questions.length - 1, s + 1))}
            disabled={!currentAnswered || isPending}
            className="ask-user-form-next"
          >
            Next →
          </button>
        ) : (
          <button
            type="submit"
            disabled={isPending || !allAnswered}
            className="ask-user-form-submit"
          >
            {isPending ? 'Submitting…' : 'Submit answers'}
          </button>
        )}
        <button
          type="button"
          onClick={onReject}
          disabled={isPending}
          className="ask-user-form-reject"
          style={{ marginLeft: 'auto' }}
        >
          Reject
        </button>
      </div>
    </form>
  );
}

function RunComposer({
  sessionId,
  disabled,
  disabledReason,
  onOptimisticSend,
  onOptimisticClear,
}: {
  sessionId: string;
  disabled: boolean;
  disabledReason: string | null;
  /**
   * fn-2.x — parent renders the pending user bubble immediately on
   * submit, before the BFF/CP round-trip + MA's SSE delivery
   * completes. Improves perceived latency on the first turn.
   */
  onOptimisticSend?: (content: string) => void;
  /** Cleared if the mutation throws; the parent removes the bubble. */
  onOptimisticClear?: () => void;
}): React.ReactElement {
  const [content, setContent] = useState('');
  const sendMutation = trpc.runs.sendMessage.useMutation();
  const submit = async (): Promise<void> => {
    const trimmedContent = content.trim();
    if (trimmedContent.length === 0) return;
    onOptimisticSend?.(trimmedContent);
    setContent('');
    try {
      await sendMutation.mutateAsync({ sessionId, content: trimmedContent });
    } catch {
      // surfaces via sendMutation.error; also clear the pending
      // bubble so the operator can resubmit (or know it failed).
      onOptimisticClear?.();
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends; Shift+Enter / Cmd+Enter inserts a newline. This is
    // the chat-first ergonomics most users expect (Anthropic Console,
    // Slack, etc.). Mod+Enter also sends as a power-user shortcut.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };
  return (
    <form
      className="run-composer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="visually-hidden" htmlFor="run-composer-input">
        Message
      </label>
      <textarea
        id="run-composer-input"
        className="run-composer-input"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={
          disabled
            ? (disabledReason ?? 'Composer disabled')
            : 'Message the agent — Enter to send, Shift+Enter for newline'
        }
        disabled={disabled || sendMutation.isPending}
        rows={2}
      />
      <div className="run-composer-footer">
        <small className="run-composer-hint">
          {content.trim().length > 0 ? (
            <span>
              <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for newline ·{' '}
              {content.length.toLocaleString()} chars
            </span>
          ) : (
            <span>
              <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for newline
            </span>
          )}
        </small>
        <button
          type="submit"
          disabled={disabled || sendMutation.isPending || content.trim().length === 0}
          className="run-composer-send"
        >
          {sendMutation.isPending ? 'Sending…' : 'Send'}
        </button>
      </div>
      {sendMutation.error !== null && (
        <p className="run-composer-error" role="alert">
          {sendMutation.error.message}
        </p>
      )}
    </form>
  );
}
